<template>
  <el-container class="layout-container">
    <!-- 侧边栏 -->
    <el-aside :width="isCollapse ? '64px' : '220px'" class="sidebar">
      <div class="logo">
        <img src="@/assets/logo.png" alt="赛吧管理后台" class="logo-image" />
        <span class="logo-text" v-if="!isCollapse">赛吧管理后台</span>
      </div>
      
      <el-menu
        :default-active="activeMenu"
        :collapse="isCollapse"
        :collapse-transition="false"
        router
        unique-opened
        class="sidebar-menu"
      >
        <template v-for="route in routes" :key="route.path">
          <el-menu-item 
            v-if="!route.children" 
            :index="route.path"
            :route="route"
          >
            <el-icon>
              <component :is="route.meta.icon" />
            </el-icon>
            <template #title>
              <span>{{ route.meta.title }}</span>
              <el-badge
                v-if="route.path === '/manual-review' && reviewPendingCount > 0"
                :value="reviewPendingCount"
                :max="99"
                class="menu-badge"
              />
            </template>
          </el-menu-item>
          
          <el-sub-menu 
            v-else 
            :index="route.path"
            :key="route.path"
          >
            <template #title>
              <el-icon>
                <component :is="route.meta.icon" />
              </el-icon>
              <span>{{ route.meta.title }}</span>
            </template>
            
            <el-menu-item 
              v-for="child in route.children" 
              :key="child.path" 
              :index="child.path"
              :route="child"
            >
              <template #title>
                {{ child.meta.title }}
              </template>
            </el-menu-item>
          </el-sub-menu>
        </template>
      </el-menu>
      
      <div class="sidebar-footer" v-if="!isCollapse">
        <div class="system-info">
          <div class="info-item">
            <span class="label">版本:</span>
            <span class="value">v1.0.0</span>
          </div>
          <div class="info-item">
            <span class="label">状态:</span>
            <span class="value status-online">运行中</span>
          </div>
        </div>
      </div>
    </el-aside>

    <!-- 主内容区 -->
    <el-container>
      <!-- 顶部导航栏 -->
      <el-header class="header">
        <div class="header-left">
          <el-button 
            link 
            @click="toggleSidebar"
            class="collapse-btn"
          >
            <el-icon :size="20">
              <component :is="isCollapse ? 'Expand' : 'Fold'" />
            </el-icon>
          </el-button>
          
          <el-breadcrumb separator="/" class="breadcrumb">
            <el-breadcrumb-item :to="{ path: '/dashboard' }">
              首页
            </el-breadcrumb-item>
            <el-breadcrumb-item 
              v-for="(item, index) in breadcrumb" 
              :key="index"
              :to="item.path"
            >
              {{ item.meta.title }}
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>

        <div class="header-right">
          <!-- 搜索框 -->
          <el-input
            v-model="searchKeyword"
            placeholder="搜索..."
            prefix-icon="Search"
            class="search-input"
            size="small"
            @keyup.enter="handleSearch"
          />
          
          <!-- 用户信息 -->
          <el-dropdown trigger="click" class="user-dropdown">
            <div class="user-info">
              <el-avatar 
                :size="32" 
                src="https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png"
                class="user-avatar"
              />
              <span class="user-name">管理员</span>
              <el-icon>
                <ArrowDown />
              </el-icon>
            </div>
            
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="handleUserCenter">
                  <el-icon><User /></el-icon>
                  个人中心
                </el-dropdown-item>
                <el-dropdown-item @click="handleAccountSettings">
                  <el-icon><Setting /></el-icon>
                  账号设置
                </el-dropdown-item>
                <el-dropdown-item divided @click="handleLogout">
                  <el-icon><SwitchButton /></el-icon>
                  退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          
          <!-- 全屏切换 -->
          <el-tooltip content="全屏" placement="bottom">
            <el-button link @click="toggleFullscreen">
              <el-icon :size="18">
                <FullScreen />
              </el-icon>
            </el-button>
          </el-tooltip>
        </div>
      </el-header>

      <!-- 主内容 -->
      <el-main class="main-content">
        <router-view />
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import api from '@/utils/api'

const routes = [
  {
    path: '/dashboard',
    meta: { title: '数据概览', icon: 'DataBoard' }
  },
  {
    path: '/activities',
    meta: { title: '活动管理', icon: 'Calendar' }
  },
  {
    path: '/users',
    meta: { title: '用户管理', icon: 'User' }
  },
  {
    path: '/teams',
    meta: { title: '战队管理', icon: 'Trophy' }
  },
  {
    path: '/orders',
    meta: { title: '订单管理', icon: 'ShoppingCart' }
  },
  {
    path: '/membership',
    meta: { title: '会员权益管理', icon: 'Medal' }
  },
  {
    path: '/sensitive-words',
    meta: { title: '敏感词管理', icon: 'Filter' }
  },
  {
    path: '/manual-review',
    meta: { title: '人工审核', icon: 'Warning' }
  },
  {
    path: '/settings',
    meta: { title: '系统设置', icon: 'Setting' }
  }
]

export default {
  name: 'Layout',
  
  setup() {
    const route = useRoute()
    const router = useRouter()
    
    const isCollapse = ref(false)
    const searchKeyword = ref('')
    const reviewPendingCount = ref(0)

    // 轮询待审核数量（每60秒一次）
    const pollReviewCount = async () => {
      try {
        const res = await api.getManualReviewPendingCount()
        reviewPendingCount.value = res.data?.count || 0
      } catch (_) {}
    }
    
    // 当前激活的菜单
    const activeMenu = computed(() => {
      const { meta, path } = route
      return path
    })
    
    // 面包屑导航
    const breadcrumb = computed(() => {
      const matched = route.matched.filter(item => item.meta && item.meta.title)
      return matched.slice(1) // 去掉首页
    })
    
    // 切换侧边栏折叠
    const toggleSidebar = () => {
      isCollapse.value = !isCollapse.value
    }
    
    // 处理搜索
    const handleSearch = () => {
      if (searchKeyword.value.trim()) {
        ElMessage.info(`搜索: ${searchKeyword.value}`)
        // 这里可以添加搜索逻辑
        searchKeyword.value = ''
      }
    }
    
    // 切换全屏
    const toggleFullscreen = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(err => {
          console.log('全屏错误:', err)
        })
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen()
        }
      }
    }
    
    // 个人中心
    const handleUserCenter = () => {
      router.push('/profile')
    }
    
    // 账号设置
    const handleAccountSettings = () => {
      router.push('/settings')
    }
    
    // 退出登录
    const handleLogout = () => {
      ElMessageBox.confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 清除token
        localStorage.removeItem('admin_token')
        localStorage.removeItem('admin_user')
        
        // 跳转到登录页
        router.push('/login')
        
        ElMessage.success('退出成功')
      }).catch(() => {
        // 取消退出
      })
    }
    
    // 监听路由变化，更新面包屑
    watch(() => route.path, () => {
      // 可以在这里添加页面访问统计等逻辑
    })

    onMounted(() => {
      pollReviewCount()
      setInterval(pollReviewCount, 60000) // 每60秒轮询
    })
    
    return {
      routes,
      isCollapse,
      searchKeyword,
      reviewPendingCount,
      activeMenu,
      breadcrumb,
      toggleSidebar,
      handleSearch,
      toggleFullscreen,
      handleUserCenter,
      handleAccountSettings,
      handleLogout
    }
  }
}
</script>

<style scoped>
.layout-container {
  height: 100vh;
}

.menu-badge {
  margin-left: 8px;
  vertical-align: middle;
}

/* 侧边栏样式 */
.sidebar {
  background: #001529;
  border-right: 1px solid #e6e6e6;
  transition: width 0.3s;
  display: flex;
  flex-direction: column;
}

/* 统一菜单样式 */
.sidebar-menu :deep(.el-menu-item) {
  color: rgba(255, 255, 255, 0.65);
  transition: all 0.3s;
}

.sidebar-menu :deep(.el-menu-item:hover) {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.1);
}

.sidebar-menu :deep(.el-menu-item.is-active) {
  color: #409EFF;
  background-color: rgba(64, 158, 255, 0.2);
}

.logo {
  height: 60px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #002140;
}

.logo-image {
  width: 32px;
  height: 32px;
}

.logo-text {
  margin-left: 12px;
  font-size: 18px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
}

.sidebar-menu {
  flex: 1;
  border-right: none;
  background: #001529;
}

.sidebar-menu:not(.el-menu--collapse) {
  width: 100%;
}

.sidebar-footer {
  padding: 16px;
  border-top: 1px solid #002140;
}

.system-info {
  color: #8c8c8c;
  font-size: 12px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.status-online {
  color: #52c41a;
}

/* 顶部导航栏样式 */
.header {
  background: white;
  border-bottom: 1px solid #e6e6e6;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
}

.collapse-btn {
  margin-right: 16px;
}

.breadcrumb {
  font-size: 14px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.search-input {
  width: 200px;
}

.user-dropdown {
  cursor: pointer;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.user-info:hover {
  background-color: #f5f7fa;
}

.user-avatar {
  flex-shrink: 0;
}

.user-name {
  font-size: 14px;
  color: #606266;
}

/* 主内容区样式 */
.main-content {
  background: #f5f7fa;
  padding: 20px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    z-index: 1000;
    left: -220px;
    transition: left 0.3s;
  }
  
  .sidebar.show {
    left: 0;
  }
  
  .header {
    padding: 0 12px;
  }
  
  .search-input {
    display: none;
  }
  
  .user-name {
    display: none;
  }
}
</style>